import { useNavigate } from 'react-router-dom'
import { VALID_COUNTRY_CODES } from '@models/ValidCountryCodes'
import { useState } from 'react'
import { UserServices } from '@services/UserServices';
import { Application } from '@core/Application';
import { Logger } from '@utils/Logger';
import { Analytics } from '@core/Analytics/Analytics';

export const useStartDemoViewModel = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState<string | null>(null);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState<string | null>(null);
    
    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState<string | null>(null);

    const [serviceError, setServiceError] = useState<string | null>(null);

    const analytics = new AnalyticsHelper()

    const validateName = () => {
        if (!name.trim()) {
            setNameError("El nombre es requerido".localized());
            return false;
        }
        setNameError(null);
        return true;
    };

    const viewDidAppear = () => {
        analytics.trackMainScreenShown()
        let user = Application.shared().user

        if (!user) {
            return
        }

        setName(user.name)
        setCompany(user.company)
        setPhone(user.mobilePhoneNumber)
        setEmail(user.email)
    }

    const validatePhone = () => {
        if (!phone.trim()) {
            setPhoneError("El número de teléfono es requerido".localized());
            return false;
        }
        
        // Check if it starts with +
        if (!phone.startsWith('+')) {
            setPhoneError("El número debe comenzar con + seguido del código de país, por ejemplo +54 en Argentina.".localized());
            return false;
        }
        
        // Remove the + and any spaces/dashes
        const phoneDigits = phone.slice(1).replace(/[\s\-]/g, '');
        
        // Check if it contains only digits after the +
        if (!/^\d+$/.test(phoneDigits)) {
            setPhoneError("El número solo puede contener dígitos después del +".localized());
            return false;
        }
        
        // Extract country code (1-3 digits)
        // Country codes can be 1, 2, or 3 digits
        let countryCode = '';
        let isValidCountryCode = false;
        
        // Try 1-digit country code (e.g., +1 for USA/Canada)
        countryCode = phoneDigits.substring(0, 1);
        if (VALID_COUNTRY_CODES.has(countryCode)) {
            isValidCountryCode = true;
        }
        
        // Try 2-digit country code if 1-digit didn't match (e.g., +54 for Argentina)
        if (!isValidCountryCode && phoneDigits.length >= 2) {
            countryCode = phoneDigits.substring(0, 2);
            if (VALID_COUNTRY_CODES.has(countryCode)) {
                isValidCountryCode = true;
            }
        }
        
        // Try 3-digit country code if 2-digit didn't match (e.g., +358 for Finland)
        if (!isValidCountryCode && phoneDigits.length >= 3) {
            countryCode = phoneDigits.substring(0, 3);
            if (VALID_COUNTRY_CODES.has(countryCode)) {
                isValidCountryCode = true;
            }
        }
        
        if (!isValidCountryCode) {
            setPhoneError("El código de país no es válido".localized());
            return false;
        }
        
        // Check minimum length (country code + number, usually at least 8 digits total)
        if (phoneDigits.length < 8) {
            setPhoneError("El número de teléfono es demasiado corto".localized());
            return false;
        }
        
        // Check maximum length (international numbers are typically max 15 digits)
        if (phoneDigits.length > 15) {
            setPhoneError("El número de teléfono es demasiado largo".localized());
            return false;
        }
        
        setPhoneError(null);
        return true;
    };
  
    const validateCompany = () => {
        if (!company.trim()) {
            setCompanyError("La empresa es requerida".localized());
            return false;
        }
        setCompanyError(null);
        return true;
    };

    const validateEmail = () => {
        const trimmedEmail = email.trim()
        
        // Check if email is empty
        if (!trimmedEmail) {
            setEmailError("El mail es requerido".localized())
            return false
        }
        
        // Email regex pattern (RFC 5322 simplified)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        
        // Check if email format is valid
        if (!emailRegex.test(trimmedEmail)) {
            setEmailError("El formato del mail es inválido".localized())
            return false
        }
        
        setEmailError(null)
        return true
    }

    const validateAll = () => {
        const isNameValid = validateName();
        const isPhoneValid = validatePhone();
        const isCompanyValid = validateCompany();
        const isEmailValid = validateEmail()
        return isNameValid && isPhoneValid && isCompanyValid && isEmailValid;
    };

    const handleSubmit = async () => {
        if (!validateAll()) {
            return false;
        }

        setLoading(true)

        try {
            setServiceError(null)
            let service = new UserServices()
            let user = await service.createNewDemoUser(
                name,
                phone,
                company,
                email,
            )

            Application.shared().setUser(user)
            if (user.status == "validated") {
                let service = new UserServices()
                try {
                    let token = await service.getToken(
                        phone,
                        "dummy"
                    )
            
                    Application.shared().setToken(token)
                } catch (error) {
                    setServiceError("Hubo un error al intentar validar tu codigo".localized())
                    setLoading(false)
                    return false
                }
                analytics.trackFormCompleted(true)
                navigate('/location', { 
                    state: { 
                        name, 
                        phone, 
                        company,
                    } 
                });
            } else {
                analytics.trackFormCompleted(false)
                navigate('/validate-number', { 
                    state: { 
                        name, 
                        phone, 
                        company 
                    } 
                });
            }
            setLoading(false)
        } catch(error) {
            Logger.logObject("error", error)
            setServiceError("Hubo un error al intentar cargar la información.".localized())
            setLoading(false)
        }

        return true;
    };

    const resetForm = () => {
        setName("");
        setPhone("");
        setCompany("");
        setEmail("");
        setNameError(null);
        setPhoneError(null);
        setCompanyError(null);
    };

    return {
        // State values
        loading,
        name,
        phone,
        company,
        email,
        nameError,
        phoneError,
        companyError,
        emailError,
        serviceError,
        
        // Setters
        setLoading,
        setName,
        setPhone,
        setCompany,
        setEmail,
        
        // Validation and actions
        validateName,
        validatePhone,
        validateCompany,
        validateEmail,
        validateAll,
        handleSubmit,
        resetForm,
        viewDidAppear,
    };
};

class AnalyticsHelper {
    private analytics: Analytics
    constructor(analytics: Analytics = Application.shared().coreDependencies.analytics) {
        this.analytics = analytics
    }

    async trackMainScreenShown() {
        this.analytics.track({
            eventType: 'AutoDemo.MainScreen.Shown',
        })
    }

    async trackFormCompleted(isReturningUser: boolean) {
        this.analytics.track({
            eventType: 'AutoDemo.MainScreen.formCompleted',
            payload: {
                isReturningUser: isReturningUser
            }
        })
    }
}